import React from 'react';
import type { BiomarkerMeta } from '../../data/biomarkerCatalogue';
import type { BiomarkerStatusResult } from '../../utils/biomarkerStatus';

/**
 * "Line with a ball" — shows where a member's value sits relative to its healthy
 * reference range. The healthy band is green and a marker (ball) sits at the
 * value. Rendered with positioned divs (not a stretched SVG) so the marker stays
 * perfectly circular at any width.
 *
 * RED MEANS DISTANCE, NOT TERRITORY. The out-of-range track used to be flooded
 * soft red from the boundary to the end of the axis, so the amount of red was a
 * property of the marker's scale rather than of the member: a healthy eGFR was
 * drawn on a bar that was three-quarters red, and a member told us he was
 * frightened by his own normal result — "you have no idea if something is bad".
 * Now nothing is red unless the member is actually outside the range, and the
 * red that does appear spans boundary→marker, so its width is how far out they
 * are. An in-range result has no red on it anywhere.
 *
 * Only render when `status.hasBar` is true (numeric value + reconciled unit +
 * a usable reference range). Sex-specific ranges are already resolved into
 * `status.ref` by computeBiomarkerStatus, so this component is sex-agnostic.
 *
 * The axis comes from `status`, NOT from `meta`. When the range is the lab's
 * rather than Melio's it is expressed in the lab's unit, and the catalogue's
 * axis bounds are in the canonical one — drawing a nmol/L folate result on a
 * µg/L axis would place both the marker and the healthy band wrongly, which is
 * exactly the class of error the bar exists to prevent.
 */

interface Props {
  meta: BiomarkerMeta;
  status: BiomarkerStatusResult;
  /** Slightly taller labels/marker for the (P1) expanded view. */
  large?: boolean;
}

const TRACK_BASE = 'rgba(26, 26, 24, 0.06)';
const HEALTHY_FILL = 'rgba(79, 93, 24, 0.5)';
// Stronger than the flood it replaces (0.24), because it now covers only the
// gap between the boundary and the marker rather than half the bar.
const DISTANCE_FILL = 'rgba(155, 47, 42, 0.38)';

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

/** Fraction (0..1) of a value along the axis, clamped to the ends. */
function axisFraction(value: number, min: number, max: number): { fraction: number; offScale: 'low' | 'high' | null } {
  if (max <= min) return { fraction: 0, offScale: null };
  const raw = (value - min) / (max - min);
  if (raw < 0) return { fraction: 0, offScale: 'low' };
  if (raw > 1) return { fraction: 1, offScale: 'high' };
  return { fraction: raw, offScale: null };
}

function formatEdge(n: number): string {
  // Trim to at most 2 dp without trailing zeros.
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

/**
 * The healthy range as a sentence — "90 and above mL/min/1.73m²".
 *
 * A one-sided range had no visual statement at all: the unit rode on the upper
 * tick label, which for an open-ended range is never drawn, so a member looking
 * at eGFR saw a bare "90" against a scale whose units were nowhere on the
 * screen. The screen-reader label has always spoken the range properly; this is
 * the same fact, given to everyone else.
 */
function healthyRangeText(status: BiomarkerStatusResult): string | null {
  const { low, high } = status.ref;
  const unit = status.axisUnit ? ` ${status.axisUnit}` : '';
  if (low != null && high != null) return `${formatEdge(low)}–${formatEdge(high)}${unit}`;
  // Unit before the direction — "90 mL/min/1.73m² and above", not "90 and above
  // mL/min/1.73m²", which is where the words land if the unit is just appended.
  if (low != null) return `${formatEdge(low)}${unit} and above`;
  if (high != null) return `up to ${formatEdge(high)}${unit}`;
  return null;
}

export default function BiomarkerRangeBar({ meta, status, large = false }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    const id = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  if (!status.hasBar || status.canonicalValue == null) return null;

  const { axisMin, axisMax, axisUnit: unit } = status;
  const { low, high } = status.ref;

  const lowFrac = low == null ? 0 : axisFraction(low, axisMin, axisMax).fraction;
  const highFrac = high == null ? 1 : axisFraction(high, axisMin, axisMax).fraction;
  const { fraction: valueFrac, offScale } = axisFraction(status.canonicalValue, axisMin, axisMax);

  const pct = (f: number) => `${(f * 100).toFixed(2)}%`;
  const ballSize = large ? 16 : 13;
  const trackHeight = large ? 8 : 6;

  // Boundary→marker, for a measured result that landed outside the band. Null
  // when in range — that is the point: nothing on the bar is red. A censored
  // result draws its own region further down and must not get both.
  const distance = status.censored || (status.status !== 'high' && status.status !== 'low')
    ? null
    : status.status === 'high'
      ? { from: highFrac, to: valueFrac }
      : { from: valueFrac, to: lowFrac };

  // Screen-reader users get the range's provenance in the same breath as the
  // verdict, matching the caption sighted users see under the bar.
  const rangeOwner = status.rangeSource === 'lab' ? 'Laboratory reference range' : 'Melio healthy range';
  const rangeText = healthyRangeText(status);

  // "less than 7 nmol/L", not "7 nmol/L" — the distinction is the whole point of
  // a censored result and must survive into the screen-reader text.
  const spokenValue = status.censored
    ? `${status.censored.direction === 'below' ? 'less than' : 'greater than'} ${status.censored.bound}${unit ? ` ${unit}` : ''}`
    : `${status.canonicalValue}${unit ? ` ${unit}` : ''}`;

  const ariaLabel = `${meta.label} ${spokenValue} — ${
    status.status === 'in_range' ? 'in range' : status.label.toLowerCase()
  }.${rangeText ? ` ${rangeOwner} ${rangeText}.` : ''}`;

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{ paddingTop: large ? 10 : 8, paddingBottom: large ? 4 : 2 }}
    >
      {/* The margin is the tick labels' room — they are positioned out of flow. */}
      <div style={{ position: 'relative', height: trackHeight, marginBottom: large ? 22 : 18 }}>
        {/* Base track */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 999,
            background: TRACK_BASE,
            overflow: 'hidden',
          }}
        >
          {/* Healthy band */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: pct(lowFrac),
              width: pct(Math.max(0, highFrac - lowFrac)),
              background: HEALTHY_FILL,
            }}
          />
          {/* How far outside the band the member is. Width IS the answer to
              "is this bad?" — a sliver for just outside, most of the track for
              a long way outside — where the flood this replaced was the same
              size either way. */}
          {distance && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: pct(distance.from),
                width: pct(Math.max(0, distance.to - distance.from)),
                background: DISTANCE_FILL,
                transition: reducedMotion ? undefined : 'width 520ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            />
          )}
        </div>

        {/* Boundary tick labels */}
        {low != null && lowFrac > 0.001 && (
          <span
            style={{
              position: 'absolute',
              top: trackHeight + 3,
              left: pct(lowFrac),
              transform: 'translateX(-50%)',
              fontSize: large ? 11 : 10,
              color: 'var(--melio-text-muted)',
              whiteSpace: 'nowrap',
            }}
          >
            {formatEdge(low)}
          </span>
        )}
        {high != null && highFrac < 0.999 && (
          <span
            style={{
              position: 'absolute',
              top: trackHeight + 3,
              left: pct(highFrac),
              transform: 'translateX(-50%)',
              fontSize: large ? 11 : 10,
              color: 'var(--melio-text-muted)',
              whiteSpace: 'nowrap',
            }}
          >
            {formatEdge(high)}
          </span>
        )}

        {/* A censored result ("<7.00") has no point to mark — the true value is
            somewhere in the region beyond the bound. Drawing a ball at the
            bound would assert a measurement the lab explicitly did not make, so
            the region is shaded and the bound gets a boundary line instead. */}
        {status.censored ? (
          <>
            <div
              style={{
                position: 'absolute',
                top: -2,
                bottom: -2,
                left: status.censored.direction === 'below' ? 0 : pct(valueFrac),
                width: pct(status.censored.direction === 'below' ? valueFrac : 1 - valueFrac),
                background: `linear-gradient(${status.censored.direction === 'below' ? '90deg' : '270deg'}, ${status.color}22, ${status.color}55)`,
                borderRadius: 999,
                border: `1px solid ${status.color}66`,
                transition: reducedMotion ? undefined : 'width 520ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: -4,
                bottom: -4,
                left: pct(valueFrac),
                width: 2,
                marginLeft: -1,
                background: status.color,
                borderRadius: 1,
              }}
            />
          </>
        ) : (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: pct(reducedMotion || mounted ? valueFrac : lowFrac + (highFrac - lowFrac) / 2),
              width: ballSize,
              height: ballSize,
              marginLeft: -ballSize / 2,
              marginTop: -ballSize / 2,
              borderRadius: '50%',
              background: status.color,
              // The halo separates the marker from the track. It matched neither
              // the card it sits on (white) nor the page (#F7F6F2) — it was cream
              // #F5F3F0, a third value — which read as a faint grey fringe.
              border: '2px solid var(--m-surface)',
              boxShadow: 'var(--e-1)',
              transition: reducedMotion ? undefined : 'left 520ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          />
        )}
        {/* Off-scale indicator */}
        {offScale && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              [offScale === 'low' ? 'left' : 'right']: 2,
              fontSize: large ? 13 : 11,
              lineHeight: 1,
              color: status.color,
              fontWeight: 700,
            }}
          >
            {offScale === 'low' ? '‹' : '›'}
          </span>
        )}
      </div>

      {/* The range, stated. Carries the provenance too, because "healthy is
          90 and above" and "that is Melio's number, not your lab's" are one
          thought — and stacking them as two grey lines said "range" twice. */}
      {rangeText && (
        <p className="text-[11px]" style={{ color: 'var(--melio-text-muted)' }}>
          {`Healthy: ${rangeText}`}
          <span aria-hidden> · </span>
          {status.rangeSource === 'lab' ? 'your laboratory’s range' : 'Melio’s optimal range'}
        </p>
      )}
    </div>
  );
}
